<template>
  <div
    class="fdz"
    :class="{ 'fdz--drag': isDragOver, 'fdz--disabled': disabled }"
    @click="openFilePicker"
    @drop.prevent="onDrop"
    @dragover.prevent="onDragOver"
    @dragenter.prevent
    @dragleave="onDragLeave"
  >
    <!-- Default look; pass your own markup through the slot to restyle it.
         The slot receives { isDragOver, openFilePicker, openFolderPicker }. -->
    <slot v-bind="slotProps">
      <div class="fdz-default" :class="{ 'fdz-default--drag': isDragOver }">
        <div class="fdz-icon">
          <i :class="['bi', icon]"></i>
        </div>
        <h3 class="fdz-title">{{ title }}</h3>
        <p v-if="hint" class="fdz-hint">{{ hint }}</p>
        <button
          v-if="folderEnabled"
          type="button"
          class="fdz-folder-btn"
          :disabled="disabled"
          @click.stop="openFolderPicker"
        >
          <i class="bi bi-folder2-open"></i> Select folder
        </button>
      </div>
    </slot>

    <input
      ref="fileInput"
      type="file"
      hidden
      :multiple="multiple"
      :accept="acceptAttr"
      :disabled="disabled"
      @click.stop
      @change="onFileInputChange"
    >
    <input
      v-if="folderEnabled"
      ref="folderInput"
      type="file"
      hidden
      webkitdirectory
      multiple
      :disabled="disabled"
      @click.stop
      @change="onFolderInputChange"
    >
  </div>
</template>

<script>
// Reusable drop target + file/folder picker. It only gathers files and emits
// them; validation, previews and uploading stay with the host component
// (see MultiImageUploader for a queue-based host, News.vue for a single file).
export default {
  name: 'FileDropZone',

  props: {
    // Allow selecting/dropping more than one file.
    multiple: {
      type: Boolean,
      default: true
    },
    // Value for the file input's `accept` attribute: an array of mime types /
    // extensions, or a ready-made string such as "image/*". It only filters
    // the native picker; hosts still validate what they receive.
    accept: {
      type: [Array, String],
      default: () => []
    },
    // Show a "Select folder" button and accept folders dropped on the zone.
    allowFolders: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Used by the default look only.
    title: {
      type: String,
      default: 'Drag files here or click to browse'
    },
    hint: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: 'bi-cloud-upload'
    }
  },

  // (files: File[], meta: { fromFolder: boolean })
  emits: ['files'],

  data() {
    return {
      isDragOver: false
    }
  },

  computed: {
    acceptAttr() {
      return Array.isArray(this.accept) ? this.accept.join(',') : this.accept
    },

    folderEnabled() {
      return this.allowFolders && this.multiple
    },

    slotProps() {
      return {
        isDragOver: this.isDragOver,
        openFilePicker: this.openFilePicker,
        openFolderPicker: this.openFolderPicker
      }
    }
  },

  methods: {
    openFilePicker() {
      if (this.disabled) return
      this.$refs.fileInput?.click()
    },

    openFolderPicker() {
      if (this.disabled) return
      this.$refs.folderInput?.click()
    },

    onFileInputChange(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (files.length > 0) this.$emit('files', files, { fromFolder: false })
    },

    onFolderInputChange(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (files.length > 0) this.$emit('files', files, { fromFolder: true })
    },

    onDragOver() {
      if (!this.disabled) this.isDragOver = true
    },

    onDragLeave(event) {
      // Ignore dragleave fired when moving over a child element.
      if (event.currentTarget.contains(event.relatedTarget)) return
      this.isDragOver = false
    },

    async onDrop(event) {
      this.isDragOver = false
      if (this.disabled) return

      const { files, fromFolder } = await this.collectDropped(event.dataTransfer)
      if (files.length > 0) this.$emit('files', files, { fromFolder })
    },

    // The drop event's DataTransfer is only valid until the handler yields, so
    // the entries are read synchronously here and walked afterwards.
    collectDropped(dataTransfer) {
      if (!dataTransfer) return Promise.resolve({ files: [], fromFolder: false })

      const plainFiles = Array.from(dataTransfer.files || [])
      const entries = Array.from(dataTransfer.items || [])
        .filter(item => item.kind === 'file')
        .map(item => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null))
        .filter(Boolean)

      if (!entries.some(entry => entry.isDirectory)) {
        return Promise.resolve({ files: plainFiles, fromFolder: false })
      }

      return Promise.all(entries.map(entry => this.readEntry(entry)))
        .then(groups => ({ files: groups.flat(), fromFolder: true }))
    },

    // Recursively collect the files under a dropped file/folder entry.
    async readEntry(entry) {
      if (entry.isFile) {
        try {
          return [await new Promise((resolve, reject) => entry.file(resolve, reject))]
        } catch {
          return []
        }
      }

      if (!entry.isDirectory) return []

      const reader = entry.createReader()
      const children = []

      // readEntries hands back at most ~100 entries per call, so keep reading
      // until it returns an empty batch.
      for (;;) {
        let batch
        try {
          batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject))
        } catch {
          break
        }
        if (batch.length === 0) break
        children.push(...batch)
      }

      const nested = await Promise.all(children.map(child => this.readEntry(child)))
      return nested.flat()
    }
  }
}
</script>

<style scoped>
.fdz {
  position: relative;
  cursor: pointer;
}

.fdz--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.fdz-default {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 3rem 2rem;
  text-align: center;
  transition: all 0.2s ease;
  margin-bottom: 1.5rem;
}

.fdz:hover .fdz-default,
.fdz-default--drag {
  border-color: #0ea5e9;
  background: rgba(14, 165, 233, 0.05);
}

.fdz-icon {
  font-size: 2.5rem;
  color: #9ca3af;
  margin-bottom: 1rem;
}

.fdz-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.fdz-hint {
  color: #6b7280;
  margin: 0;
  font-size: 0.875rem;
}

.fdz-folder-btn {
  margin-top: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #f3f4f6;
  color: #374151;
  padding: 0.4rem 0.9rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
}

.fdz-folder-btn:hover:not(:disabled) {
  background: #e5e7eb;
}
</style>
